import { forwardRef, type HTMLAttributes, type ReactElement, type Ref } from 'react';
import { cn } from '../lib/utils.js';
import {
  PopoverContentImpl,
  PopoverRoot,
  PopoverTriggerImpl,
  Slot,
  composeEventHandlers,
  useComposedRefs,
  usePopoverContext,
  type ContentProps,
  type PopoverRootProps,
  type TriggerProps,
} from './popover-internals.js';

/**
 * A box that hangs off a trigger, styled to match `dropdown-menu.tsx`. Built on the native
 * `popover` attribute (see `popover-internals.tsx`), with the same exports and props it had when
 * it wrapped Radix.
 *
 * IT EXISTS BECAUSE A DROPDOWN MENU IS THE WRONG CONTAINER FOR A ROW THAT HAS THINGS IN IT. A
 * menu item dismisses the menu when it is chosen and takes roving arrow-key focus, so a follow-up
 * row carrying a tick, a customer's name and a call button cannot live in one: ticking the task
 * would close the panel, and Tab would not reach the phone number.
 *
 * A popover is just a box that hangs off a trigger. Everything inside it is ordinary markup with
 * ordinary focus order, which is what a working surface needs.
 */

export function Popover(props: PopoverRootProps): ReactElement {
  return <PopoverRoot {...props} />;
}

export const PopoverTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function PopoverTrigger(props, ref) {
    return <PopoverTriggerImpl ref={ref} {...props} />;
  },
);

/** Closes the popover it sits in. With `asChild`, any element (a link, say) does the closing. */
export const PopoverClose = forwardRef<HTMLButtonElement, TriggerProps>(function PopoverClose(
  { asChild, onClick, ...props },
  ref,
) {
  const context = usePopoverContext('PopoverClose');
  const Comp = (asChild ? Slot : 'button') as 'button';
  return (
    <Comp
      type={asChild ? undefined : 'button'}
      {...props}
      ref={ref}
      onClick={composeEventHandlers(onClick, () => context.setOpen(false))}
    />
  );
});

/** Positions the content against this element instead of the trigger. */
export const PopoverAnchor = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { asChild?: boolean }
>(function PopoverAnchor({ asChild, ...props }, forwardedRef) {
  const context = usePopoverContext('PopoverAnchor');
  const ref = useComposedRefs<HTMLElement>(forwardedRef as Ref<HTMLElement>, context.anchorRef);
  const Comp = (asChild ? Slot : 'div') as 'div';
  return <Comp {...props} ref={ref} />;
});

export const PopoverContent = forwardRef<HTMLDivElement, ContentProps>(function PopoverContent(
  { className, align = 'end', sideOffset = 6, collisionPadding = 12, ...props },
  ref,
) {
  return (
    <PopoverContentImpl
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        // `p-0` undoes the user-agent padding every `[popover]` element gets.
        'z-50 rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md outline-none',
        className,
      )}
      {...props}
    />
  );
});
